import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import PrepareTrackingDataHelper from '@Services/PrepareTrackingDataHelper';
import ToggleButton from '@Components/Results/reactions/reaction-details/ToggleButton';
import UtilsHelper from '@Components/Results/services/utils-helper.service';
import ReactReactionDetailsHelper from '@Components/Results/services/react-reaction-details-helper.service';
import FullText from '@Components/Results/reactions/reaction-details/FullText';
import CitationsCustomLinkComponent from '@Components/Results/reactions/reaction-details/citations-custom-link.component';
import { getValueByNs } from '@Hooks/LibraryInitialization/MoreUnderscore';
import { LinkElement } from '@els/els-react--link-element';
import CitedCountLink from '@Components/Results/CitedCountLink';

import './ReactionCitations.scss';

function CitationsButtonsComponent({
  metadata,
  citation,
  index,
  isAbstractExpanded,
  toggleAbstractExpanded,
  onIssueCitedCounterClick = () => null,
  onFullTextLinkClick = () => null,
  onAbstractOpen = () => null,
}) {
  const detailsButtonRef = useRef(null);
  const debouncedAbstractClick = useRef(null);

  // Create debounced function and update it when dependencies change
  useEffect(() => {
    debouncedAbstractClick.current = debounce(
      () => {
        ReactReactionDetailsHelper.openTitlesAbstractsDialog(citation, metadata);
      },
      500,
      { leading: true }
    );

    // Cleanup previous debounced function
    return () => {
      if (debouncedAbstractClick.current) {
        debouncedAbstractClick.current.cancel();
      }
    };
  }, [citation, metadata]);

  const abstractClick = useCallback(() => {
    debouncedAbstractClick.current?.();
  }, []);

  const getTrackingProps = useCallback(
    () => ({
      'data-click-delegation': '',
      'data-tracking': 'true',
      'data-tracking-context': metadata.context,
      'data-tracking-preview': getValueByNs({ metadata }, 'metadata.isViewPreview'),
      'data-tracking-track-link-location-in-results': true,
      'data-tracking-from-rt': metadata.fromRT,
      ...PrepareTrackingDataHelper.prepare(citation),
    }),
    [citation, metadata]
  );

  const goToContext = useCallback((context, item, requestParams) => {
    if (detailsButtonRef.current?.getAttribute('disabled')) return;
    detailsButtonRef.current?.setAttribute('disabled', 'true');
    ReactReactionDetailsHelper.goToContext(context, item, requestParams);
  }, []);

  const { oldTrackingDisabled } = metadata;
  const preparedTrackingData = getTrackingProps();
  const fullTextTrackingData = oldTrackingDisabled
    ? {}
    : {
        ...preparedTrackingData,
        'data-tracking-type': 'linkout',
        'data-tracking-action': 'full text',
        'data-doi': UtilsHelper.stripTags(citation.doi),
      };
  const citedTrackingData = oldTrackingDisabled
    ? {}
    : {
        ...preparedTrackingData,
        'data-tracking-type': 'linkout',
        'data-tracking-action': 'cited',
      };
  const abstractLinkTrackingData = oldTrackingDisabled
    ? {}
    : {
        ...preparedTrackingData,
        'data-tracking-type': 'contentview',
        'data-tracking-action': 'abstract',
      };

  return (
    <div className="result-detail-row-btns els-btn-group-left u-els-display-inline">
      {citation.abstractText && (
        <ToggleButton
          ariaControls={`reaction-citations-abstract-${index}`}
          expanded={!!isAbstractExpanded}
          toggleExpand={toggleAbstractExpanded}
          expandedLabel="Abstract"
          collapsedLabel="Abstract"
          prepareTrackingData={citation}
          dataTrackingAction="abstract"
          isViewPreview={metadata.isViewPreview}
          className="els-btn-xs blue-compact e2e-abstract-text"
        />
      )}
      {citation.fullTextCustom && (
        <CitationsCustomLinkComponent citation={citation} fullTextTrackingData={fullTextTrackingData} onClick={onFullTextLinkClick} />
      )}
      {!citation.fullTextCustom && <FullText citation={citation} trackingAttributes={{ fullTextTrackingData }} metadata={metadata} />}
      {citation.issueCitedByCounter > 0 && (
        <CitedCountLink
          data-e2e="e2e-cited-button"
          citedCount={citation.issueCitedByCounter}
          citedUrl={citation.citedBy}
          {...citedTrackingData}
          onClick={() => onIssueCitedCounterClick(citation)}
          className="e2e-cited rx-no-wrap u-els-font-size-base"
        />
      )}
      {citation && (
        <LinkElement
          iconSprite="ChevronRight"
          data-e2e="e2e-details-button"
          href="#"
          iconRight
          iconSize="xs"
          ref={detailsButtonRef}
          onClick={(event) => {
            event.preventDefault();
            goToContext('citations', citation, metadata);
          }}
          className="e2e-details rx-no-wrap u-els-font-size-base">
          Details
        </LinkElement>
      )}
      {citation.showAbstractLink && (
        <LinkElement
          iconSprite="ChevronRight"
          data-e2e="e2e-abstract-button"
          href="#"
          iconRight
          iconSize="xs"
          onClick={(event) => {
            event.preventDefault();
            onAbstractOpen(citation);
            abstractClick();
          }}
          {...abstractLinkTrackingData}
          className="e2e-abstract rx-no-wrap u-els-font-size-base">
          Abstract
        </LinkElement>
      )}
    </div>
  );
}

CitationsButtonsComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  citation: PropTypes.object.isRequired,
  index: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]),
  isAbstractExpanded: PropTypes.bool.isRequired,
  toggleAbstractExpanded: PropTypes.func.isRequired,
  onIssueCitedCounterClick: PropTypes.func,
  onFullTextLinkClick: PropTypes.func,
  onAbstractOpen: PropTypes.func,
};

export default CitationsButtonsComponent;
